import type { BaseModelAttributes } from './base';
import { I18nLabels } from './common';
import { Locale } from './locale';

export enum BgmCategory {
  SYSTEM = 'SYSTEM',
  USER = 'USER'
}

export const BgmCategoryLabels: I18nLabels<BgmCategory> = {
  [BgmCategory.SYSTEM]: { [Locale.ZH_CN]: '系统', [Locale.EN_US]: 'System' },
  [BgmCategory.USER]: { [Locale.ZH_CN]: '用户', [Locale.EN_US]: 'User' },
};

export enum BgmGenStatus {
  GENERATING_COMPLETED = 'GENERATING_COMPLETED',
  GENERATING = 'GENERATING',
  FAILED = 'FAILED'
}

export const BgmGenStatusLabels: I18nLabels<BgmGenStatus> = {
  [BgmGenStatus.GENERATING_COMPLETED]: { [Locale.ZH_CN]: '生成完成', [Locale.EN_US]: 'Completed' },
  [BgmGenStatus.GENERATING]: { [Locale.ZH_CN]: '生成中', [Locale.EN_US]: 'Generating' },
  [BgmGenStatus.FAILED]: { [Locale.ZH_CN]: '生成失败', [Locale.EN_US]: 'Failed' },
};

// 硬编码的BGM类型
export interface IBgmType {
  id: number;
  seq: number;
  code: string;
  desc: string;
  descEn: string;
}

export const BGM_TYPES: IBgmType[] = [
  { id: 1, seq: 1, code: 'bgm_type_gufengxianxia', desc: '古风悠扬', descEn: 'Ancient Chinese' },
  { id: 2, seq: 2, code: 'bgm_type_xuanyikongbu', desc: '悬疑恐怖', descEn: 'Suspense & Horror' },
  { id: 3, seq: 3, code: 'bgm_type_dushi', desc: '都市', descEn: 'Urban' },
  { id: 4, seq: 4, code: 'bgm_type_pangbodaqi', desc: '磅礴大气', descEn: 'Epic & Grand' },
  { id: 5, seq: 5, code: 'bgm_type_qingsongsuhuan', desc: '轻松舒缓', descEn: 'Relaxing' },
  { id: 6, seq: 6, code: 'bgm_type_dongganjiezou', desc: '动感节奏', descEn: 'Upbeat Rhythm' },
  { id: 7, seq: 7, code: 'bgm_type_big_movies', desc: '史诗电影', descEn: 'Epic Cinema' },
  { id: 8, seq: 8, code: 'bgm_type_chinese_traditional', desc: '中国传统', descEn: 'Chinese Traditional' },
  { id: 9, seq: 9, code: 'bgm_type_festival', desc: '节日庆典', descEn: 'Festival' },
  { id: 10, seq: 10, code: 'bgm_type_kid_music', desc: '儿童音乐', descEn: "Children's Music" },
  { id: 11, seq: 11, code: 'bgm_type_light_music', desc: '轻音乐', descEn: 'Light Music' },
  { id: 12, seq: 12, code: 'bgm_type_meditation', desc: '冥想放松', descEn: 'Meditation' },
  { id: 13, seq: 13, code: 'bgm_type_mystery', desc: '神秘氛围', descEn: 'Mysterious' },
  { id: 14, seq: 14, code: 'bgm_type_passion', desc: '激情澎湃', descEn: 'Passionate' },
  { id: 15, seq: 15, code: 'bgm_type_rockroll', desc: '摇滚', descEn: 'Rock & Roll' },
  { id: 16, seq: 16, code: 'bgm_type_sleeping_song', desc: '助眠音乐', descEn: 'Sleep Music' },
  { id: 17, seq: 17, code: 'bgm_type_sports', desc: '运动健身', descEn: 'Sports & Fitness' },
  { id: 18, seq: 18, code: 'bgm_type_strings', desc: '弦乐', descEn: 'Strings' },
  { id: 19, seq: 19, code: 'bgm_type_symphony', desc: '交响乐', descEn: 'Symphony' }
];

export const BGM_TYPE_MAP: Record<number, IBgmType> = BGM_TYPES.reduce((acc, type) => {
  acc[type.id] = type;
  return acc;
}, {} as Record<number, IBgmType>);

/**
 * 根据语言获取 BGM 类型的显示名称
 */
export function getBgmTypeDesc(bgmType: IBgmType, locale: Locale): string {
  return locale === Locale.EN_US ? bgmType.descEn : bgmType.desc;
}

export interface IBgm extends BaseModelAttributes {
  seq: number;
  bgmTypeId: number;
  name: string;
  duration: number;
  audioUrl?: string;
  bgmCategory: BgmCategory;
  genStatus: BgmGenStatus;
  coverImageUrl?: string;
  bgmType?: IBgmType;
}

export interface CreateBgmRequest {
  name: string;
  bgmTypeId: number;
  duration: number;
  audioUrl?: string;
  bgmCategory?: BgmCategory;
  genStatus?: BgmGenStatus;
  coverImageUrl?: string;
  seq?: number;
}

export interface UpdateBgmRequest {
  name?: string;
  bgmTypeId?: number;
  duration?: number;
  audioUrl?: string;
  bgmCategory?: BgmCategory;
  genStatus?: BgmGenStatus;
  coverImageUrl?: string;
  seq?: number;
}

export interface BgmQuery {
  search?: string;
  bgmTypeId?: number;
  bgmCategory?: BgmCategory;
  genStatus?: BgmGenStatus;
}